import React, { useState, useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// COMPONENTS
import IconDefinitionEscalatorDefault from "../iconDefinition/IconDefinitionEscalatorDefault";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { useCanOpenControlPanel } from "../../../customHooks/useScopedPermission";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UUID
import { stringToUuidT } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

function MonitoringLiftEscalatorFullView({
  lift,
  bank,
  serverId,
  channelId,
  enableActions,
}: {
  lift: LiftInterface;
  bank: BankInterface;
  serverId: string;
  channelId: string;
  enableActions: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [liftId, setLiftId] = useState<string>("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode,
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = previewFaultServiceMode || socketMessageLift.faults;

  const serviceModeInfo = useMemo(
    () =>
      getServiceModeDisplay(
        socketMessageLift.serviceMode?.messageType ?? null,
        socketMessageLift.serviceMode?.message?.message?.message ?? null,
      ),
    [socketMessageLift.serviceMode],
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  // The lift name opens the panel only when at least one of the panel's tabs
  // would render for this role (see CONTROL_PANEL_TAB_GATES); MASTER ADMIN always.
  // Deliberately NOT tied to the Banks switch: the Roles editor's Monitoring
  // Dashboard Banks block shows the lifts, it does not make their names clickable.
  const canOpenLiftControlPanel = useCanOpenControlPanel("lift");

  const onClickOpenModalElevator = (e: any) => {
    // With no tab to show, the lift name is inert; the per-tab permissions only
    // matter once the panel IS open.
    if (!canOpenLiftControlPanel) return;
    if (enableActions) {
      getDrawerPlacement(e);
      dispatch(
        updateModalControlPanelTab({
          liftType: LiftType[LiftType.ESCALATOR],
          // enableActions gates the Actions tab's existence, so it's always
          // safe to default to it here.
          tab: "ACTIONS",
        }),
      );
      dispatch(updateLiftSelected(lift));
      dispatch(updateBankSelected(bank));
      dispatch(updateChannelId(channelId));
      dispatch(updateServerId(serverId));
      dispatch(updateOpenModalControlPanelLift(true));
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (lift && lift.id && String(lift.id) !== "") {
      setLiftId(String(lift.id));
    }
  }, [lift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {/* Top container */}
      <div
        className={`monitoringView__topViewContainer__${theme} generalStyles__flexCenter ${
          hasFaults
            ? "monitoringView__dangerElevator monitoringView__dangerElevator__top monitoringView__dangerElevator__bottom"
            : ""
        }`}
        style={{
          height: "50px",
          width: "45px",
          margin: "5px",
          marginTop: "1px",
          textAlign: "center",
          paddingBottom: "8px",
        }}
      >
        <IconDefinitionEscalatorDefault iconSize={40} mapViewClass="" lift={lift} />
      </div>

      {/* Bottom container */}
      <div
        role="button"
        tabIndex={0}
        onKeyDown={emptyFunctionDeclaration}
        onClick={onClickOpenModalElevator}
        className=""
        style={{
          fontSize: "9px",
          fontWeight: "bolder",
          textAlign: "center",
        }}
      >
        {/* Lift name */}
        <div>
          <Tooltip placement="top" title={lift.name}>
            <span className={hasFaults ? "generalStyles__error" : ""}>
              {String(lift.name ?? "")
                .toUpperCase()
                .substring(0, 6)}
            </span>
          </Tooltip>
        </div>
        {/* Fault tag */}
        {hasFaults && (
          <div style={{ textAlign: "center" }}>
            <Tooltip placement="top" title="Fault">
              <Tag
                style={{
                  fontSize: "10px",
                  width: "90%",
                  marginLeft: "5%",
                  marginRight: "5%",
                }}
                color="red"
              >
                FAULT
              </Tag>
            </Tooltip>
          </div>
        )}
        {/* Service mode tag */}
        {(serviceModeInfo || previewFaultServiceMode) && (
          <div>
            <Tooltip
              placement="top"
              title={serviceModeInfo ? serviceModeInfo.displayName : "Energy Saving"}
            >
              <Tag
                style={{
                  marginTop: "5px",
                  width: "90%",
                  marginLeft: "5%",
                  marginRight: "5%",
                  fontSize: "10px",
                }}
                color={serviceModeInfo ? serviceModeInfo.color || "default" : "blue"}
              >
                {serviceModeInfo ? serviceModeInfo.abbreviation.substring(0, 4) : "ENER"}
              </Tag>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonitoringLiftEscalatorFullView;
